// src/components/agent/TeddyChatKit.tsx
//
// Teddy — the BENCHMARK in-product agent, rendered through OpenAI
// ChatKit (self-hosted "custom server" integration). The web component
// talks to our own /api/chatkit endpoint (FastAPI + openai-chatkit SDK
// + LiteLLM, with a per-domain system prompt + tool registry so
// Teddy's answers and capabilities adapt to whatever page the
// operator is on — catalog, orders, sales, imports, etc.).
//
// Layout: a floating round button bottom-right with the live
// animated Teddy (96 px) perches bottom-right, idles. Open: a
// standard ChatKit panel rises from the bottom. Animation refs the
// live animation pack committed at public/teddy/ (see
// docs/milestones/teddy-animation-handoff.md).
//
// Transitions are deliberately theatrical: when the launcher is
// clicked it does a quick "spark" (scale + spin), then the bubble
// pops in with a scale + rotation + fade. On close the bubble
// scales down and fades. The header plays the existing 'hello'
// animation. Reduced motion skips the motion (the chat simply
// appears), per the handoff contract.
//
// State is driven by real application state (chatkit control), not by
// decorative timers: hello on first open, listening while the composer
// has focus, speaking while the model streams tokens, idle otherwise.

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChatKit, useChatKit } from "@openai/chatkit-react";
import { X } from "lucide-react";
import { onOpenAgent } from "@/lib/agent/agentControl";
import { authHeaders } from "@/lib/api";
import { useAuth } from "@/components/auth/AuthContext";
import { PublicTeddyChat } from "@/components/agent/PublicTeddyChat";
import { cn } from "@/lib/cn";
import { domainForPath, greetingForPath } from "@/lib/agent/pageContext";
import { TeddyBot, type TeddyState } from "@/components/agent/TeddyBot";

const DOMAIN_KEY =
  (import.meta.env.VITE_CHATKIT_DOMAIN_KEY as string | undefined) ??
  "dev-local";

export interface TeddyChatKitProps {
  initiallyOpen?: boolean;
}

export function TeddyChatKit({ initiallyOpen = false }: TeddyChatKitProps) {
  const auth = useAuth();
  const [open, setOpen] = useState(initiallyOpen);
  // Anonymous visitors get the M4 public Teddy (five free catalog
  // questions, then a polite sign-in request); authenticated users get
  // the full ChatKit assistant.
  const isAuthenticated = auth.isReady && auth.isAuthenticated;
  const [publicState, setPublicState] = useState<"idle" | "thinking" | "speaking">("idle");
  const [hasFocus, setHasFocus] = useState(false);
  const [hasThread, setHasThread] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Re-evaluate the current page on every render so the chat fetch
  // always carries the latest path. The server uses it to pick
  // the right system prompt + tool registry for Teddy.
  const location = useLocation();
  const { i18n } = useTranslation();
  const domain = useMemo(
    () => domainForPath(location.pathname),
    [location.pathname],
  );

  // Greeting shown on a fresh thread. The chatkit SDK only reads
  // startScreen on mount, so we re-render the whole <ChatKit> tree
  // whenever the route changes — that's the simplest way to make
  // the greeting reflect the current menu/section ("Ask about the
  // catalog" on /catalog, "Need help with imports?" on
  // /admin/imports, etc.). The remount is triggered by the
  // `key` on the wrapping <ChatKit> below.
  const startScreen = useMemo(
    () => greetingForPath(location.pathname, i18n.language),
    [location.pathname, i18n.language],
  );

  const { control, focusComposer } = useChatKit({
    api: {
      url: "/api/chatkit",
      domainKey: DOMAIN_KEY,
      // A02: /api/chatkit requires authentication. Inject the BFF CSRF
      // header (cookie session) or bearer token on every ChatKit call,
      // plus the current page so the server can adapt the agent.
      fetch: (input: RequestInfo | URL, init?: RequestInit) =>
        fetch(input, {
          ...init,
          credentials: "same-origin",
          headers: {
            ...(init?.headers ?? {}),
            ...authHeaders(),
            "X-Page-Path": location.pathname,
            "X-Page-Domain": domain,
          },
        }),
    },
    theme: { colorScheme: "dark" },
    // Re-mount on every route change so the greeting reflects the
    // current section (the chatkit SDK only reads startScreen on
    // initial mount).
    startScreen,
    // Drive Teddy's state from real chatkit lifecycle events:
    //  - onThreadChange: hello on a fresh thread
    //  - onResponseStart: speaking while the model produces tokens
    //  - onResponseEnd:   back to idle (the composer focus picks up next)
    onThreadChange: (event: { threadId: string | null }) => {
      setHasThread(Boolean(event.threadId));
    },
    onResponseStart: () => setStreaming(true),
    onResponseEnd: () => setStreaming(false),
    onError: (event: { error?: { message?: string } }) => {
      // Anonymous visitors use the public chat — a 401 from the
      // authenticated ChatKit endpoint is expected and not an error.
      if (!isAuthenticated) return;
      const msg = event?.error?.message ?? "Model request failed.";
      setChatError(msg);
    },
  });

  // Cmd/Ctrl+K toggles, Esc closes, "Ask Teddy" header button opens.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    const teardown = onOpenAgent(() => setOpen(true));
    return () => {
      window.removeEventListener("keydown", onKey);
      teardown();
    };
  }, [open]);

  // Observe the chatkit-rendered composer for focus. The DOM shape is
  // internal to chatkit-react, so we hunt for the first text input
  // inside the bubble and attach focus/blur listeners. We only
  // subscribe when the bubble is open.
  useEffect(() => {
    if (!open) return;
    const root = document.querySelector(
      'div[class*="fixed"][class*="z-\\[60\\]"]',
    );
    if (!root) return;
    const composer = root.querySelector(
      'input, textarea',
    ) as HTMLInputElement | HTMLTextAreaElement | null;
    if (!composer) return;
    inputRef.current = composer;
    const onFocus = () => setHasFocus(true);
    const onBlur = () => setHasFocus(false);
    composer.addEventListener("focus", onFocus);
    composer.addEventListener("blur", onBlur);
    return () => {
      composer.removeEventListener("focus", onFocus);
      composer.removeEventListener("blur", onBlur);
    };
  }, [open, hasThread]);

  // Drive Teddy's state from real chat/public state — never decorative.
  const state: TeddyState = !isAuthenticated
    ? publicState === "thinking"
      ? "thinking"
      : publicState === "speaking"
        ? "speaking"
        : "idle"
    : streaming
      ? "speaking"
      : !hasThread && open
        ? "hello"
        : hasFocus
          ? "listening"
          : "idle";

  // Show-state machine for the open/close animation:
  //   "open"      bubble mounted, pop-in animation playing
  //   "shown"     bubble mounted, normal resting state
  //   "close"     bubble mounted, fade-out playing
  //   "closed"    launcher (96 px circle) — ALWAYS the closed state;
  //               an earlier "minimised pill" variant made the launcher
  //               shrink after the first close, which read as a bug.
  const [show, setShow] = useState<
    "closed" | "open" | "shown" | "close"
  >(initiallyOpen ? "shown" : "closed");
  // Schedule post-animation transitions on a ref so the in-flight
  // setTimeouts survive an effect re-run when `hasUsed` (or any
  // other state) changes.
  const timers = useRef<{ open: number | null; close: number | null }>({
    open: null,
    close: null,
  });
  useEffect(() => {
    if (open && (show === "closed" || show === "close")) {
      setShow("open");
      if (timers.current.open !== null) clearTimeout(timers.current.open);
      timers.current.open = window.setTimeout(() => {
        setShow("shown");
        timers.current.open = null;
      }, 420);
      return;
    }
    if (!open && (show === "shown" || show === "open")) {
      setShow("close");
      if (timers.current.close !== null) clearTimeout(timers.current.close);
      timers.current.close = window.setTimeout(() => {
        setShow((current) => (current === "close" ? "closed" : current));
        timers.current.close = null;
      }, 220);
      return;
    }
  }, [open, show]);
  const [chatError, setChatError] = useState<string | null>(null);

  const bubbleBase =
    "flex flex-col overflow-hidden rounded-xl border border-border bg-bg-canvas shadow-2xl";
  const bubbleAnim =
    show === "open"
      ? "teddy-pop-in"
      : show === "close"
        ? "teddy-pop-out"
        : "";

  return (
    <div className="fixed bottom-5 right-5 z-[60]">
      {/* Launcher: the 96 px circle is the single closed state — after
       * closing the chat the SAME launcher comes back (no smaller
       * "minimised" pill; the size change read as a bug). */}
      {show === "closed" && (
        <button
          type="button"
          aria-label="Open Teddy, your BENCHMARK shopping assistant"
          onClick={() => {
            setOpen(true);
            setChatError(null);
            setTimeout(() => focusComposer(), 80);
          }}
          className="flex h-[96px] w-[96px] items-center justify-center overflow-hidden rounded-full border border-border bg-bg-canvas shadow-xl transition-transform duration-200 ease-out hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-benchmark-teal"
        >
          <TeddyBot
            state="idle"
            size={96}
            alt="Teddy, your BENCHMARK shopping assistant"
          />
        </button>
      )}

      {/* Bubble: mounted for the open/close animations, then fully gone. */}
      {(show === "open" || show === "shown" || show === "close") && (
        <div
          className={cn(
            bubbleBase,
            "h-[540px] w-[380px] max-w-[92vw]",
            bubbleAnim,
          )}
        >
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <TeddyBot
              state={state}
              size={64}
              alt="Teddy, your BENCHMARK shopping assistant"
            />
            <span className="text-sm font-510 text-fg-primary">Teddy</span>
            <span className="rounded bg-benchmark-teal/15 px-1.5 py-0.5 text-[10px] text-benchmark-teal-bright">
              Cool bot !
            </span>
            <button
              type="button"
              aria-label="Close Teddy"
              onClick={() => setOpen(false)}
              className="ml-auto rounded p-1 text-fg-tertiary hover:bg-bg-hover hover:text-fg-primary"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
          {chatError && (
            <div
              role="alert"
              className="mx-3 mt-2 flex items-start gap-2 rounded-md border border-accent-rose/30 bg-accent-rose/10 p-2 text-[11px] text-accent-rose"
            >
              <span className="font-510">Teddy is offline.</span>
              <span className="text-accent-rose/80">{chatError}</span>
              <button
                type="button"
                onClick={() => setChatError(null)}
                className="ml-auto text-fg-quaternary hover:text-fg-primary"
                aria-label="Dismiss error"
              >
                <X className="h-3 w-3" strokeWidth={1.5} />
              </button>
            </div>
          )}
          <div className="min-h-0 flex-1">
            {isAuthenticated ? (
              <ChatKit control={control} style={{ height: "100%", width: "100%" }} />
            ) : (
              <PublicTeddyChat onActivity={setPublicState} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
